import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import ReactImageGallery from 'react-image-gallery';

import { propTypes } from '../../../util/types';
import { useIntl } from '../../../util/reactIntl';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../../util/routes';
import {
  AspectRatioWrapper,
  Button,
  IconCloseOutline,
  IconArrowHead,
  ResponsiveImage,
} from '../../../components';
import FavouriteButton from '../../../components/FavouriteButton/FavouriteButton';

// Copied directly from
// `node_modules/react-image-gallery/styles/css/image-gallery.css`. The
// copied file is left unedited, and all the overrides are defined in
// the component CSS file below.
import './image-gallery.css';

import css from './ListingImageGallery.module.css';

const IMAGE_GALLERY_OPTIONS = {
  showPlayButton: false,
  disableThumbnailScroll: true,
};
/**
 * The ListingImageGallery component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {Array<propTypes.image>} props.images - The images
 * @param {string} [props.imageAltBase] - Product title base for image alt text
 * @param {Array<string>} props.imageVariants - The image variants for normal display
 * @param {Array<string>} [props.fullscreenVariants] - The image variants for fullscreen (defaults to imageVariants)
 * @param {Array<string>} props.thumbnailVariants - The thumbnail variants
 * @returns {JSX.Element} listing image gallery component
 */
const ListingImageGallery = props => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const galleryRef = useRef(null);
  const thumbnailsRef = useRef(null);
  const intl = useIntl();
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const { rootClassName, className, images, imageAltBase, listingId, imageVariants, fullscreenVariants, thumbnailVariants, statusOverlayLabel, onManageDisableScrolling } = props;
  const fsVariants = fullscreenVariants || imageVariants;
  const thumbVariants = thumbnailVariants || imageVariants;
  const aspectWidth = 1;
  const aspectHeight = 1;
  const items = images.map((img, i) => {
    return {
      // We will only use the image resource, but react-image-gallery
      // requires the `original` key from each item.
      original: '',
      alt: intl.formatMessage(
        { id: 'ListingImageGallery.imageAltText' },
        { title: imageAltBase || 'Listing', index: i + 1, count: images.length }
      ),
      thumbnail: img.attributes?.variants?.[thumbVariants[0]],
      image: img,
      itemIndex: i,
    };
  });
  // Gallery image display sizes (square, 1:1):
  //   Mobile  (≤859):  full viewport width
  //   Tablet  (860–1023): VW − 48 px (24 px padding each side)
  //   Desktop (1024–1271): VW − 540 px (details panel beside image)
  //   Desktop (≥1272): capped at 732 px
  const imageSizesMaybe = isFullscreen
    ? {}
    : { sizes: '(max-width: 859px) 100vw, (max-width: 1023px) calc(100vw - 48px), (max-width: 1271px) calc(100vw - 540px), 732px' };
  const handleOpenFullscreen = () => {
    if (!isFullscreen) {
      galleryRef.current?.fullScreen();
      setTimeout(() => {
        galleryRef.current?.slideToIndex(activeIndex);
      }, 0);
    }
  };

  const handleCloseFullscreen = () => {
    if (isFullscreen) {
      if (galleryRef.current?.exitFullScreen) {
        galleryRef.current.exitFullScreen();
      } else if (galleryRef.current?.toggleFullScreen) {
        galleryRef.current.toggleFullScreen();
      }
    }
  };

  const handleMainImageKeyDown = event => {
    if (isFullscreen) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleOpenFullscreen();
    }
  };

  const handleFullscreenBackdropClick = event => {
    if (!isFullscreen) {
      return;
    }

    const clickedImage = event.target.closest(`.${css.itemFullscreenImage}`);
    const clickedNav = event.target.closest(`.${css.fullscreenNav}`);

    if (!clickedImage && !clickedNav) {
      handleCloseFullscreen();
    }
  };

  const renderItem = item => {
    if (isFullscreen) {
      return (
        <div className={css.itemWrapperFullscreen} onClick={handleFullscreenBackdropClick}>
          <div className={css.itemCenteringFullscreen}>
            {/* width/height null: intrinsic attrs would cap the image at the
                smallest variant's size instead of filling the viewport */}
            <ResponsiveImage
              rootClassName={css.itemFullscreenImage}
              image={item.image}
              alt={item.alt}
              variants={fsVariants}
              {...imageSizesMaybe}
              width={null}
              height={null}
            />
          </div>
        </div>
      );
    }

    return (
      <AspectRatioWrapper
        width={aspectWidth || 1}
        height={aspectHeight || 1}
        className={classNames(css.itemWrapper, css.itemWrapperInteractive)}
        onClick={handleOpenFullscreen}
        onKeyDown={handleMainImageKeyDown}
        role="button"
        tabIndex={0}
      >
        <div className={css.itemCentering}>
          <ResponsiveImage
            rootClassName={css.item}
            image={item.image}
            alt={item.alt}
            variants={imageVariants}
            {...(item.itemIndex === 0 ? { fetchpriority: 'high' } : { loading: 'lazy' })}
            {...imageSizesMaybe}
          />
        </div>
      </AspectRatioWrapper>
    );
  };

  const onScreenChange = isFull => {
    setIsFullscreen(isFull);
    // The modal-fallback fullscreen (iOS Safari) leaves the page scrollable underneath
    if (onManageDisableScrolling) {
      onManageDisableScrolling('ListingPage.imageGallery', isFull);
    }
  };

  // Re-enable page scrolling if the gallery unmounts while in fullscreen
  useEffect(() => {
    return () => {
      if (onManageDisableScrolling) {
        onManageDisableScrolling('ListingPage.imageGallery', false);
      }
    };
  }, []);
  const renderFullscreenButton = (onClick, isFullscreen) => {
    return isFullscreen ? (
      <Button
        onClick={onClick}
        rootClassName={css.close}
        title={intl.formatMessage({ id: 'ListingImageGallery.closeModalTitle' })}
      >
        <IconCloseOutline />
      </Button>
    ) : null;
  };

  const handleSlide = index => {
    setActiveIndex(index);

    if (thumbnailsRef.current) {
      const thumbButtons = thumbnailsRef.current.querySelectorAll('button');
      const activeThumb = thumbButtons[index];
      if (activeThumb) {
        const inlineMode = index === 0 ? 'start' : index === thumbButtons.length - 1 ? 'end' : 'center';
        activeThumb.scrollIntoView({ behavior: 'smooth', inline: inlineMode, block: 'nearest' });
      }
    }
  };

  const slideTo = index => {
    if (!galleryRef.current) {
      return;
    }

    const clampedIndex = Math.max(0, Math.min(index, items.length - 1));
    setActiveIndex(clampedIndex);
    galleryRef.current.slideToIndex(clampedIndex);
  };

  const handlePrev = () => slideTo(activeIndex - 1);
  const handleNext = () => slideTo(activeIndex + 1);

  const handleBack = () => {
    if (window.history.length > 1) {
      history.goBack();
    } else {
      history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, {}));
    }
  };

  const handleThumbnailClick = index => () => slideTo(index);

  const renderLeftNav = (onClick, disabled) => {
    const className = classNames(css.fullscreenNav, css.fullscreenNavLeft, {
      [css.fullscreenNavHidden]: !isFullscreen,
    });

    return (
      <button className={className} disabled={disabled} onClick={onClick}>
        <IconArrowHead direction="left" size="big" rootClassName={css.fullscreenNavIcon} />
      </button>
    );
  };

  const renderRightNav = (onClick, disabled) => {
    const className = classNames(css.fullscreenNav, css.fullscreenNavRight, {
      [css.fullscreenNavHidden]: !isFullscreen,
    });

    return (
      <button className={className} disabled={disabled} onClick={onClick}>
        <IconArrowHead direction="right" size="big" rootClassName={css.fullscreenNavIcon} />
      </button>
    );
  };

  if (items.length === 0) {
    return <ResponsiveImage className={css.noImage} image={null} variants={[]} alt="" />;
  }

  const classes = classNames(rootClassName || css.root, className);
  const canGoPrev = activeIndex > 0;
  const canGoNext = activeIndex < items.length - 1;

  const showImageCount = !isFullscreen && items.length > 0;

  return (
    <div className={classes}>
      <div className={css.mediaWrapper}>
        <ReactImageGallery
          ref={galleryRef}
          additionalClass={css.gallery}
          items={items}
          renderItem={renderItem}
          onSlide={handleSlide}
          onScreenChange={onScreenChange}
          renderLeftNav={renderLeftNav}
          renderRightNav={renderRightNav}
          renderFullscreenButton={renderFullscreenButton}
          showThumbnails={false}
          showNav={isFullscreen}
          {...IMAGE_GALLERY_OPTIONS}
        />
        {!isFullscreen ? (
          <button
            type="button"
            className={css.backButton}
            onClick={handleBack}
            aria-label={intl.formatMessage({ id: 'ListingImageGallery.backButtonLabel' })}
          >
            <svg width="16" height="12" viewBox="0 0 22 17" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M8.2844 15.25L1.8922 8.5L8.2844 1.75M1.8922 8.5H20.4414"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        ) : null}
        {showImageCount ? (
          <span className={css.imageCountBadge}>
            {activeIndex + 1}/{items.length}
          </span>
        ) : null}
        {listingId && !statusOverlayLabel ? (
          <FavouriteButton
            listingId={listingId}
            variant="card"
            className={css.galleryFavouriteButton}
          />
        ) : null}
        {statusOverlayLabel && !isFullscreen ? (
          <div className={css.statusOverlay}>
            <span className={css.statusOverlayLabel}>{statusOverlayLabel}</span>
          </div>
        ) : null}
      </div>
      <div className={css.thumbnailControls}>
        <button
          type="button"
          className={classNames(css.thumbNav, { [css.thumbNavDisabled]: !canGoPrev })}
          onClick={handlePrev}
          disabled={!canGoPrev}
          aria-label="Previous image"
        >
          <IconArrowHead direction="left" size="big" />
        </button>
        <div
          className={classNames(css.thumbList, {
            [css.thumbListCentered]: images.length <= 4,
            [css.thumbListMuted]: !!statusOverlayLabel,
          })}
          ref={thumbnailsRef}
        >
          {images.map((img, index) => {
            const buttonClasses = classNames(css.thumbButton, {
              [css.thumbButtonActive]: index === activeIndex,
            });

            return (
              <button
                type="button"
                key={img.id?.uuid || index}
                className={buttonClasses}
                onClick={handleThumbnailClick(index)}
                aria-label={intl.formatMessage(
                  { id: 'ListingImageGallery.imageThumbnailAltText' },
                  { index: index + 1, count: images.length }
                )}
              >
                <ResponsiveImage
                  rootClassName={css.thumbImage}
                  image={img}
                  alt=""
                  variants={thumbVariants}
                  sizes="84px"
                  loading="lazy"
                />
              </button>
            );
          })}
        </div>
        <button
          type="button"
          className={classNames(css.thumbNav, { [css.thumbNavDisabled]: !canGoNext })}
          onClick={handleNext}
          disabled={!canGoNext}
          aria-label="Next image"
        >
          <IconArrowHead direction="right" size="big" />
        </button>
      </div>
    </div>
  );
};

export default ListingImageGallery;
